<template>
  <SvgSprite />
  <div class="as-content-page as-match-detail" ref="asMatchDetail">
    <TabsIcon
      :tabs="tabs"
      :activeTab="activeTab"
      :isMin="isChatTyping"
      @change="changeTab"
    />
    <div
      id="as-match-detail-content"
      class="as-match-detail-content"
      :class="{
        'as-hide-content': isHideContent,
      }"
      :style="{
        height: `${heightContent}px`,
      }"
    >
      <div v-show="activeTab === 'chat'">
        <IframeChat />
      </div>

      <div v-if="activeTab === 'statistics'">
        <Transition name="as-fade" mode="out-in">
          <StatisticsList
            v-if="tabLoading.statistics"
            :match="match"
            :statistics="statistics"
          />
          <StatisticsListSkeleton v-else />
        </Transition>
      </div>
      <div v-else-if="activeTab === 'overview'">
        <Transition name="as-fade" mode="out-in">
          <IncidentsList
            v-if="tabLoading.overview"
            :match="match"
            :incidents="incidents"
          />
          <IncidentsListSkeleton v-else />
        </Transition>
      </div>
      <div v-else-if="activeTab === 'bracket'">
        <Transition name="as-fade" mode="out-in">
          <BracketTabList
            v-if="tabLoading.bracket"
            :match="match"
            :tournament="tournament"
          />
          <IncidentsListSkeleton v-else />
        </Transition>
      </div>
      <div v-else-if="activeTab === 'h2h'" class="as-badminton">
        <Transition name="as-fade" mode="out-in">
          <H2hList
            v-if="tabLoading.h2h || tabLoading.next_match || isObjectNotEmpty(h2h)"
            :match="match"
            :h2h="h2h"
          />
          <H2hListSkeleton v-else />
        </Transition>
      </div>
      <div v-else-if="activeTab === 'next_match'" class="as-badminton">
        <div
          :class="`as-parent-h2h as-parent-h2h__item-h-auto as-parent-h2h-${siteName}`"
        >
          <Transition name="as-fade" mode="out-in">
            <NextMatchList
              v-if="tabLoading.h2h || tabLoading.next_match || isObjectNotEmpty(h2h)"
              :match="match"
              :h2h="h2h"
            />
            <H2hListSkeleton v-else />
          </Transition>
        </div>
      </div>
      <div
        v-else-if="activeTab === 'match_other'"
        :class="`as-parent-h2h as-parent-h2h__item-h-auto as-parent-h2h-${siteName}`"
      >
        <Transition name="as-fade" mode="out-in">
          <MatchOther
            v-if="tabLoading.match_other"
            :matchLiveData="matchOtherData"
            :matchId="matchId"
          />
          <H2hListSkeleton v-else />
        </Transition>
      </div>
      <div v-else-if="activeTab === 'odd'">
        <OddTab
          :odd="odd"
          :oddOld="oddOld"
          :tabKey="tabKey.odd"
        />
      </div>
      <div v-else-if="activeTab === 'information'">
        <Information :match="match" />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import IncidentsListSkeleton from "~src/components/skeleton/result-popup/badminton/IncidentsListSkeleton.vue";
import StatisticsListSkeleton from "~src/components/skeleton/result-popup/StatisticsListSkeleton.vue";
import H2hListSkeleton from "~src/components/skeleton/result-popup/H2hListSkeleton.vue";
import LineupTabSkeleton from "~src/components/skeleton/result-popup/LineupTabSkeleton.vue";
import {
  matchStatistics,
  matchH2h,
  matchDetail,
  matchDetailLive,
  getRealtimeDetail,
  seasonBracketMatch,
} from "~src/services/SpapiService";
import { getOddsMatch } from "~src/services/OddService";
import { getMatchLive } from "~src/services/RemoteLiveService";
import { initializeData, mapMatchData } from "~src/helpers/badminton";

import { addMatchDetailLive, mapTeamToH2H } from "~src/helpers/tennis";
import type {
  IMatch,
  IOddMatch,
  IItemString,
  IStatsJsonData,
  IIncidentRealTime,
} from "~src/interfaces/badminton";
import IncidentsList from "~src/components/incidents/badminton/IncidentsList.vue";
import StatisticsList from "~src/components/statistics/badminton/StatisticsList.vue";
import H2hList from "~src/components/h2h/badminton/H2hList.vue";
import Information from "~src/components/matches/badminton/Information.vue";
import MatchOther from "~src/components/matches/badminton/MatchOther.vue";
import NextMatchList from "~src/components/matches/tennis/NextMatchList.vue";
import OddTab from "~src/components/odds/OddTab.vue";
import IframeChat from "~src/components/IframeChat.vue";
import IconStats from "~src/components/icons/menus/IconStats.vue";
import IconSummary from "~src/components/icons/menus/IconSummary.vue";
import IconChat from "~src/components/icons/menus/IconChat.vue";
import IconH2h from "~src/components/icons/menus/IconH2h.vue";
import IconMatchOther from "~src/components/icons/menus/IconMatchOther.vue";
import IconOdd from "~src/components/icons/menus/IconOdd.vue";
import IconLiveMatch from "~src/components/icons/menus/IconLiveMatch.vue";
import IconLive from "~src/components/icons/menus/IconLive.vue";
import IconSchematic from "~src/components/icons/menus/tennis/IconSchematic.vue";
import SvgSprite from "~src/components/matches/SvgSprite.vue";
import MatchEnum from "~src/enums/basketball/MatchEnum";
import { useMatchDetailStore } from "~src/stores/MatchDetailStore";
import TabsIcon from "~src/components/TabsIcon.vue";
import BracketTabList from "~src/components/bracket/tennis/BracketTabList.vue";
import type { ITournamentJsonData } from "~src/interfaces/tennis";
import type { IH2h, IMatchLiveData } from "~src/interfaces/tennis";
import { isObjectNotEmpty } from "~src/helpers";

export default defineComponent({
  name: "matchDetail",
  components: {
    StatisticsListSkeleton,
    IncidentsListSkeleton,
    H2hListSkeleton,
    LineupTabSkeleton,
    IncidentsList,
    StatisticsList,
    H2hList,
    MatchOther,
    NextMatchList,
    OddTab,
    Information,
    IframeChat,
    SvgSprite,
    TabsIcon,
    BracketTabList,
  },
  props: {
    matchId: {
      type: String,
    },
    apiLiveUrl: {
      type: String,
    },
  },
  data() {
    const matchDetailStore = useMatchDetailStore();

    return {
      tournament: {} as ITournamentJsonData | {},
      matchDetailStore,
      match: {} as IMatch,
      incidents: {} as IIncidentRealTime,
      statistics: [] as IStatsJsonData[] | any,
      h2h: {} as IH2h[] | {},
      standing: {},
      lineups: {},
      shootingPointData: null as any,
      detailShooting: null as any,
      data: null,
      odd: [] as IOddMatch[],
      oddOld: [] as IOddMatch[],
      matchOtherData: {} as IMatchLiveData,
      matchOthers: [] as Array<IMatch>,
      isLoading: false,
      isLoadingInterval: false,
      isShowChat: true,
      tabs: [
        {
          key: "statistics",
          title: "Thông số",
          icon: IconStats,
        },
        {
          key: "overview",
          title: "Diễn biến",
          icon: IconSummary,
        },
        {
          key: "bracket",
          title: "Sơ đồ",
          icon: IconSchematic,
        },
        {
          key: "chat",
          title: "Chat",
          icon: IconChat,
        },
        {
          key: "h2h",
          title: "Đối đầu",
          icon: IconH2h,
        },
        {
          key: "match_other",
          title: "Trận khác",
          icon: IconLive,
        },
        {
          key: "next_match",
          title: "Trận sắp tới",
          icon: IconMatchOther,
        },
        {
          key: "odd",
          title: "Tỷ lệ kèo",
          icon: IconOdd,
        },
        {
          key: "information",
          title: "TT trận",
          icon: IconLiveMatch,
        },
      ],
      tabLoading: {} as IItemString,
      tabKey: {} as IItemString,
    };
  },
  computed: {
    activeTab(): string {
      return this.matchDetailStore.getActiveTab;
    },
    isChatTyping(): boolean {
      return this.matchDetailStore.getIsChatTyping;
    },
    isHideContent(): boolean {
      return this.matchDetailStore.getIsHideContent;
    },
    heightContent(): number {
      return this.matchDetailStore.getHeightContent;
    },
    sportType() {
      // @ts-ignore
      return this.gSportType;
    },
    siteName() {
      // @ts-ignore
      return this.gSiteName;
    },
  },
  async created() {
    try {
      const rsMatch = await matchDetail(this.matchId, this.sportType);
      this.match =
        rsMatch.data.data && rsMatch.data.data.data
        ? rsMatch.data.data.data
        : rsMatch.data.data;

      await this.changeTab("chat");
    } catch (e) {
      console.log("Load match detail error", e);
    }
  },
  async mounted() {
    const element = this.$refs.asMatchDetail as HTMLElement;
    if (element) {
      // @ts-ignore
      this.matchDetailStore.setScreenWidth(
        element?.getBoundingClientRect().width
      );
    }

    setInterval(async () => {
      if (
        this.match.status_id !== MatchEnum.STATUS_END &&
        !this.isLoadingInterval &&
        this.activeTab !== "odd"
      ) {
        this.isLoadingInterval = true;
        await this.changeTab(this.activeTab, true);
        this.isLoadingInterval = false;
      }
    }, 21000);

    setInterval(async () => {
      if (
        this.match.status_id !== MatchEnum.STATUS_END &&
        !this.isLoadingInterval &&
        this.activeTab === "odd"
      ) {
        this.isLoadingInterval = true;
        await this.changeTab(this.activeTab, true);
        this.isLoadingInterval = false;
      }
    }, 5000);
  },
  methods: {
    async changeTab(tabName: string, isInterval = false) {
      this.matchDetailStore.setActiveTab(tabName);
      const matchId = this.matchId;
      const seasonId = this.match?.season_id ?? "";
      this.isLoading = true;

      if (tabName === "statistics") {
        try {
          const { data: { data: statistics = [] } } = await matchStatistics(matchId, "badminton");
          this.statistics = statistics?.length ? mapMatchData(statistics) : [initializeData()];
        } catch (e) {
          console.log("Load statistics error", e);
        }
      } else if (tabName === "overview") {
        try {
          const { data: { data: incidents = { match: {}, data: [] } } } = await getRealtimeDetail(matchId, this.sportType);
          this.incidents = incidents;
        } catch (e) {
          console.log("Load incidents error", e);
        }
      } else if (tabName === "h2h" || tabName === "next_match") {
        try {
          const { data: { data: h2h = {} } } = await matchH2h(matchId, this.sportType);
          const __h2h = mapTeamToH2H(h2h, this.sportType);
          this.h2h = __h2h;
        } catch (e) {
          console.log("Load h2h error", e);
        }
      } else if (tabName === "match_other") {
        try {
          let [rsMatch, rsLive] = await Promise.all([
            getMatchLive(this.apiLiveUrl, this.sportType, this.siteName),
            matchDetailLive(this.sportType),
          ]);

          const teams = rsMatch.data.teams ?? [];
          const matches = await addMatchDetailLive(
            rsMatch.data.matches,
            rsLive.data.results,
            teams
          );

          this.matchOtherData = {
            competitions: rsMatch.data.competitions,
            teams: teams,
            commentators: rsMatch.data.commentators,
            matches,
          } as IMatchLiveData;
        } catch (e) {
          console.log("Load match other error", e);
        }
      } else if (tabName === "bracket") {
        try {
          const { data: { data: tournament = {} } } = await seasonBracketMatch(seasonId, this.sportType);
          this.tournament = tournament;
        } catch (e) {
          console.log("Load bracket error", e);
        }
      } else if (tabName === "odd") {
        try {
          const { data: { data: odd = [] } } = await getOddsMatch(this.sportType, this.matchId);
          this.oddOld = JSON.parse(JSON.stringify(this.odd));
          this.odd = odd && odd.length ? odd : [];
        } catch (e) {
          console.log("Load odd error", e);
        }
      }

      this.isLoading = false;
      this.tabKey[tabName] = Date.now();
      this.tabLoading[tabName] = true;
    },
    isObjectNotEmpty,
  },
});
</script>

<style scoped></style>
